import React, { useEffect, useState } from 'react'
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import style from "./Reports.module.scss"
import { Button } from 'react-bootstrap';
import { ChartNoAxesCombined, User, TrendingUp, FileChartLine, ChartBarIncreasing, ArrowDownToDot, Download, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import fetchDailyReports from '../../services/report/fetchDailyReport.js';
import fetchMonthlyReports from '../../services/report/fetchMonthlyReport.js';
import fetchRangeReport from '../../services/report/fetchRangeReport.js';
import handleErrorToast from '../../utils/handleErrorToasts.js';
import fetchCustomerReport from '../../services/report/fetchCustomerReport.js';
import REPORT_TOASTS from '../../utils/constants/toastMesages/reportToasts.js';
import exportReportToExcel from '../../utils/exportReortToExcel.js';
import { APP_CONFIG } from '../../utils/runtimeConfig.js';

/**
 * Reports component to display daily and monthly reports including total sales,
 * order breakdown, cashier performance, and more.
 *
 * @component
 * @returns {JSX.Element} Rendered Reports component.
 */
function Reports() {

  //States for storing mobile number of the person you want to get data of
  const [mobileNumber, setMobileNumber] = useState("");

  //States for saving daily and monthly reports
  const [dailyReports, setDailyReports] = useState(null);
  const [monthlyReports, setMonthlyReports] = useState(null);

  //States for saving start data and end date 
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  //State for saving reporting range data that comes from backend
  const [reportData, setReportData] = useState(null);

  //State for saving customer data fetched by entering mobile number
  const [personData, setPersonData] = useState(null);

  useEffect(() => {
    //Function for getting daily reports 
    const DailyReports = async () => {
      try {
        const dailyResponse = await fetchDailyReports();
        setDailyReports(dailyResponse);
      }
      catch (error) {
        toast.error(REPORT_TOASTS.DAILY_REPORT_NOT_FETCHED);
      }
    }

    //Function for getting monthly reports
    const MonthlyReports = async () => {
      try {
        const response = await fetchMonthlyReports();
        setMonthlyReports(response);
      }
      catch (error) {
        toast.error(error.message);
      }
    }

    DailyReports();
    MonthlyReports();
  }, [])

  //Handler function for generating report according to startdate and end date
  const fetchDataInRange = async () => {
    if (!startDate || !endDate) {
      toast.warn("Please enter both start and end date");
    }

    try {
      const response = await fetchRangeReport(startDate, endDate);
      setReportData(response);
    }
    catch (error) {
      handleErrorToast(error);
    }
  }

  //Handler function for generating report according to customer mobile number
  const fetchByMobileNumber = async () => {
    if (!mobileNumber || mobileNumber.length < 10) {
      toast.warn("Please enter a mobile number");
    }
    try {
      const response = await fetchCustomerReport(mobileNumber);
      setPersonData(response);
    }
    catch (error) {
      handleErrorToast(error);
    }
  }

  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = String(today.getFullYear()).slice(-2);

  return (
    <div className={`${style.containerBg} p-4 w-100 d-flex flex-column gap-4`}>
      {/* Summary Cards */}
      <div className='d-flex gap-3 justify-content-start flex-wrap'>
        <div className={`${style.dailySales} card p-3 d-flex flex-row gap-3 justify-content-between align-items-center shadow-sm flex-grow-1`} style={{ minWidth: '250px', borderRadius: "10px" }}>
          <div>
            <span className='text-muted small'>Daily Sales</span>
            <h3 className='mb-0'>₹{dailyReports?.data?.totalSales?.toLocaleString() ?? '0'}</h3>
          </div>
          <div className={`${style.dailySalesGradient} ${style.blueGradient}  rounded `}>
            <ChartNoAxesCombined color='white' size={47} />
          </div>
        </div>

        <div className={`${style.monthlySales} card p-3 d-flex flex-row gap-3 justify-content-between align-items-center shadow-sm flex-grow-1`} style={{ minWidth: '250px', borderRadius: "10px" }}>
          <div>
            <span className='text-muted small'>Monthly Sales</span>
            <h3 className='mb-0'>₹{monthlyReports?.data?.totalSales?.toLocaleString() ?? '0'}</h3>
          </div>
          <div className={`${style.dailySalesGradient} ${style.purpleGradient} rounded p-2`}>
            <TrendingUp color='white' size={37} />
          </div>
        </div>

        <div className={`${style.totalSales} card p-3 d-flex flex-row gap-3 align-items-center justify-content-between shadow-sm flex-grow-1`} style={{ minWidth: '250px', borderRadius: "10px" }}>
          <div>
            <span className='text-muted small'>Total Orders</span>
            <h3 className='mb-0'>{dailyReports?.data?.totalOrders?.toLocaleString() ?? '0'}</h3>
          </div>
          <div className={`${style.dailySalesGradient} ${style.greenGradient} rounded p-2`}>
            <ChartBarIncreasing color='white' size={38} />
          </div>
        </div>

        <div className={`${style.avgSales} card p-3 d-flex flex-row gap-3 align-items-center justify-content-between shadow-sm flex-grow-1`} style={{ minWidth: '250px', borderRadius: "10px" }}>
          <div>
            <span className='text-muted small'>Avg. Order Value</span>
            <h3 className='mb-0'>
              ₹
              {dailyReports?.data?.totalSales != null && dailyReports?.data?.totalOrders
                ? (dailyReports.data.totalSales / dailyReports.data.totalOrders).toFixed(2)
                : '0.00'}
            </h3>
          </div>
          <div className={`${style.dailySalesGradient}  ${style.orangeGradient} rounded p-2`}>
            <FileChartLine color='white' size={36} />
          </div>
        </div>
      </div>

      {/* Reports Tabs */}
      <div className={`${style.scrollWrapper} card p-3 shadow-sm overflow-auto`} style={{ borderRadius: "10px" }}>
        <Tabs
          defaultActiveKey="Daily-Reports"
          id="reports-tabs"
          className={`${style.customUnderlines} mb-3`}
          fill
        >
          <Tab eventKey="Daily-Reports" title="Daily Reports">
            <div className='pt-3'>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h5 className='fw-bold mb-0'>Daily sales report</h5>
                <Button
                  variant='success'
                  size='sm'
                  onClick={() => exportReportToExcel({
                    reportType: "daily",
                    reportData: dailyReports?.data,
                    fileName: "Daily_Report"
                  })}>
                  <Download size={18} className='me-2' />
                  Export Report
                </Button>
              </div>

              <div className='mb-4'>
                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <div className={`${style.totalSalesDiv} card p-3 h-100`}>
                      <p className={`${style.totalSalesText}  small mb-1 fw-bold`}>Total sales today</p>
                      <h4 className={`${style.totalSalesValue} fw-bold`}>₹{dailyReports?.data?.totalSales?.toLocaleString() ?? '0'}</h4>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className={`${style.totalOrdersDiv} card p-3 h-100`}>
                      <p className={`${style.totalOrdersText} small mb-1 fw-bold`}>Total Orders</p>
                      <h4 className={`${style.totalOrderValue} fw-bold`}>{dailyReports?.data?.totalOrders?.toLocaleString() ?? '0'}</h4>
                    </div>
                  </div>
                </div>

                <div className='row g-4'>
                  <div className='col-lg-6'>
                    <h6 className='fw-bold pb-2'>Order type breakdown</h6>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <div className={`card ${style.dineInDiv} p-3 h-100`}>
                          <div className='d-flex justify-content-between'>
                            <label className='fw-bold small'>Dine-In</label>
                            <div className={style.dineInDot}></div>
                          </div>

                          <div className='d-flex justify-content-between mt-2'>
                            <span className='text-muted'>Orders</span>
                            <span>{dailyReports?.data?.orderTypeBreakdown?.dineIn?.count ?? 0}</span>
                          </div>
                          <div className='d-flex justify-content-between'>
                            <span className='text-muted'>Total</span>
                            <span>₹{dailyReports?.data?.orderTypeBreakdown?.dineIn?.total?.toLocaleString() ?? '0'}</span>
                          </div>
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <div className={`${style.takeAwayDiv} card p-3 h-100`}>
                          <div className='d-flex justify-content-between'>
                            <label className='fw-bold small'>Take-Away</label>
                            <div className={style.takeAwayDot}></div>
                          </div>

                          <div className='d-flex justify-content-between mt-2'>
                            <span className='text-muted'>Orders</span>
                            <span>{dailyReports?.data?.orderTypeBreakdown?.takeAway?.count ?? 0}</span>
                          </div>
                          <div className='d-flex justify-content-between'>
                            <span className='text-muted'>Total</span>
                            <span>₹{dailyReports?.data?.orderTypeBreakdown?.takeAway?.total?.toLocaleString() ?? '0'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <h6 className='fw-bold pb-2'>Cashier Performance</h6>
                    <div className='card' style={{ borderRadius: "10px" }}>
                      <div className={`${style.performanceHeader} p-2 small fw-bold`}>
                        Today's Performance Report
                      </div>
                      <div className={`${style.performanceBorder}`}></div>
                      <div className='p-2'>
                        {dailyReports?.data?.cashierSales &&
                          Object.entries(dailyReports.data.cashierSales).map(([cashierName, details]) => (
                            <div key={cashierName} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                              <div>
                                <h6 className='fw-bold mb-1 align-items-center'><span className={style.userIcon}><User size={20} /></span> <span>{cashierName ?? 'NA'}</span></h6>
                                <span className='text-muted small'>{details.orders ?? 0} orders processed</span>
                              </div>
                              <span className='fw-bold'>₹{details.total?.toLocaleString() ?? '0'}</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='mb-4'>
                <h6 className='fw-bold pb-2'>Item sales breakdown</h6>
                <div className="table-responsive" style={{ borderRadius: "10px", border: "1px solid #E0E0E0", overflow: "hidden" }}>
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Item Name</th>
                        <th className="text-center">Quantity Sold</th>
                        <th className="text-end">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dailyReports?.data?.itemSales &&
                        Object.entries(dailyReports.data.itemSales).map(([itemName, details], idx) => (
                          <tr key={itemName} style={{ backgroundColor: idx % 2 !== 0 ? "#f8f9fa" : "white" }}>
                            <td>{itemName}</td>
                            <td className="text-center">
                              <span
                                className={style.dailyTableQuantity}
                              >
                                {details.quantity ?? 0}
                              </span>
                            </td>
                            <td className="text-end">₹ {details.total?.toLocaleString() ?? "0"}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Tab>

          <Tab eventKey="Monthly-Reports" title="Monthly Reports">
            <div className='pt-3'>
              <div className='d-flex justify-content-between align-items-center mb-4'>
                <h5 className='fw-bold mb-0'>Monthly sales report</h5>
                <Button
                  variant='success'
                  size='sm'
                  onClick={() => exportReportToExcel({
                    reportType: "monthly",
                    reportData: monthlyReports?.data,
                    fileName: "Monthly_Report"
                  })}>
                  <Download size={18} className='me-2' />
                  Export Report
                </Button>
              </div>

              <div className='mb-4'>
                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <div className={`${style.totalSalesDiv} card p-3 h-100`}>
                      <p className={`${style.totalSalesText}  small mb-1 fw-bold`}>Total sales Monthly</p>
                      <h4 className={`${style.totalSalesValue} fw-bold`}>₹{monthlyReports?.data?.totalSales?.toLocaleString() ?? '0'}</h4>
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className={`${style.totalOrdersDiv} card p-3 h-100`}>
                      <p className={`${style.totalOrdersText} small mb-1 fw-bold`}>Total Orders</p>
                      <h4 className={`${style.totalOrderValue} fw-bold`}>{monthlyReports?.data?.totalOrders?.toLocaleString() ?? '0'}</h4>
                    </div>
                  </div>
                </div>

                <div className='row g-4'>
                  <div className='col-lg-6'>
                    <h6 className='fw-bold pb-2'>Order type breakdown</h6>
                    <div className='row g-3'>
                      <div className='col-md-6'>
                        <div className={`card ${style.dineInDiv} p-3 h-100`}>
                          <div className='d-flex justify-content-between'>
                            <label className='fw-bold small'>Dine-In</label>
                            <div className={style.dineInDot}></div>
                          </div>
                          <div className='d-flex justify-content-between mt-2'>
                            <span className='text-muted'>Orders</span>
                            <span>{monthlyReports?.data?.orderTypeBreakdown?.dineIn?.count ?? 0}</span>
                          </div>
                          <div className='d-flex justify-content-between'>
                            <span className='text-muted'>Total</span>
                            <span>₹{monthlyReports?.data?.orderTypeBreakdown?.dineIn?.total?.toLocaleString() ?? '0'}</span>
                          </div>
                        </div>
                      </div>
                      <div className='col-md-6'>
                        <div className={`${style.takeAwayDiv} card p-3 h-100`}>
                          <div className='d-flex justify-content-between'>
                            <label className='fw-bold small'>Take-Away</label>
                            <div className={style.takeAwayDot}></div>
                          </div>
                          <div className='d-flex justify-content-between mt-2'>
                            <span className='text-muted'>Orders</span>
                            <span>{monthlyReports?.data?.orderTypeBreakdown?.takeAway?.count ?? 0}</span>
                          </div>
                          <div className='d-flex justify-content-between'>
                            <span className='text-muted'>Total</span>
                            <span>₹{monthlyReports?.data?.orderTypeBreakdown?.takeAway?.total?.toLocaleString() ?? '0'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='col-lg-6'>
                    <h6 className='fw-bold pb-2'>Cashier Performance</h6>
                    <div className='card' style={{ borderRadius: "10px" }}>
                      <div className={`${style.performanceHeader} p-2 small fw-bold`}>
                        Monthly Performance Report
                      </div>
                      <div className={`${style.performanceBorder}`}></div>
                      <div className='p-2'>
                        {monthlyReports?.data?.cashierSales &&
                          Object.entries(monthlyReports?.data?.cashierSales).map(([cashierName, details]) => (
                            <div key={cashierName} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                              <div>
                                <h6 className='fw-bold mb-1'><span className={style.userIcon}><User size={25} /></span> <span>{cashierName ?? 'NA'}</span></h6>
                                <span className='text-muted small'>{details.orders ?? 0} orders processed</span>
                              </div>
                              <span className='fw-bold'>₹{details.total?.toLocaleString() ?? '0'}</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>

                  <div className='col-lg-6'>
                    <h6 className='fw-bold pb-2'>Daily Sales</h6>
                    <div className='card'>
                      <div className={`${style.performanceHeader} p-2 small fw-bold`}>
                        Daily sales
                      </div>
                      <div className={`${style.performanceBorder}`}></div>
                      <div className={`${style.monthlySales} p-2`}>
                        {monthlyReports?.data?.dailySales &&
                          Object.entries(monthlyReports?.data?.dailySales).map(([date, details]) => (
                            <div key={date} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                              <div>
                                <span className='d-flex gap-2 align-items-center'><span className={style.calendarDesign}><Calendar size={25} /></span> <h6 className='fw-bold mb-1'>{date}/{month}/{year}</h6></span>
                                <span className='text-muted small'>{details.orders ?? 0} orders processed</span>
                              </div>
                              <span className='fw-bold'>₹{details.total?.toLocaleString() ?? '0'}</span>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='mb-4'>
                <h6 className='fw-bold pb-2'>Item sales breakdown</h6>
                <div className="table-responsive" style={{ borderRadius: "10px", border: "1px solid #E0E0E0", overflow: "hidden" }}>
                  <table className="table table-hover mb-0">
                    <thead className='table-light'>
                      <tr>
                        <th>Item Name</th>
                        <th className='text-center'>Quantity Sold</th>
                        <th className='text-end'>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyReports?.data?.itemSales &&
                        Object.entries(monthlyReports?.data?.itemSales).map(([itemName, details], idx) => (
                          <tr key={itemName} style={{ backgroundColor: idx % 2 !== 0 ? "#f8f9fa" : "white" }}>
                            <td>{itemName}</td>
                            <td className='text-center'>
                              <span className={style.dailyTableQuantity}>
                                {details.quantity ?? 0}
                              </span>
                            </td>
                            <td className='text-end'>₹ {details.total?.toLocaleString() ?? '0'}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Tab>

          <Tab eventKey="Date-Range" title="Date Range">
            <div className='pt-3'>
              <h5 className='fw-bold mb-4'>Custom date range report</h5>

              <div className='card p-4 mb-4'>
                <div className='row g-3 align-items-center'>
                  <div className='col-md-4'>
                    <label className='form-label'>Start Date</label>
                    <input
                      value={startDate}
                      className='form-control'
                      type="date"
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className='col-md-4'>
                    <label className='form-label'>End Date</label>
                    <input
                      value={endDate}
                      className='form-control'
                      type="date"
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div style={{ paddingTop: "30px" }} className='col-md-4 d-flex align-items-end gap-2'>
                    <Button
                      onClick={() => {
                        if (new Date(startDate) > new Date(endDate)) {
                          toast.warn("Start date can't be bigger than end date");
                          return; // prevent fetch if dates are invalid
                        }
                        fetchDataInRange();
                      }}
                      variant='primary' className='flex-grow-1'>
                      Generate Report
                    </Button>
                    <Button variant='outline-danger'>
                      <ArrowDownToDot size={18} />
                    </Button>
                  </div>
                </div>
              </div>

              <div >
                {!reportData ? (
                  <div className={`${style.selectRangeText} card p-5 text-center`}><p className='mb-0'>Select a date range to generate report</p></div>
                ) : (
                  <div className='pt-3'>
                    <div className='d-flex justify-content-between align-items-center mb-4'>
                      <h5 className='fw-bold mb-0'>Daily sales report</h5>
                      <Button
                        variant='success'
                        size='sm'
                        onClick={() => exportReportToExcel({
                          reportType: "range",
                          reportData: reportData?.data,
                          fileName: `Report_${startDate}_to_${endDate}`
                        })}>
                        <Download size={18} className='me-2' />
                        Export Report
                      </Button>
                    </div>

                    <div className='mb-4'>
                      <div className='row g-3 mb-4'>
                        <div className='col-md-6'>
                          <div className={`${style.totalSalesDiv} card p-3 h-100`}>
                            <p className={`${style.totalSalesText}  small mb-1 fw-bold`}>Total sales</p>
                            <h4 className={`${style.totalSalesValue} fw-bold`}>₹{reportData?.data?.totalSales?.toLocaleString() ?? '0'}</h4>
                          </div>
                        </div>
                        <div className='col-md-6'>
                          <div className={`${style.totalOrdersDiv} card p-3 h-100`}>
                            <p className={`${style.totalOrdersText} small mb-1 fw-bold`}>Total Orders</p>
                            <h4 className={`${style.totalOrderValue} fw-bold`}>{reportData?.data?.totalOrders?.toLocaleString() ?? '0'}</h4>
                          </div>
                        </div>
                      </div>

                      <div className='row g-4'>
                        <div className='col-lg-6'>
                          <h6 className='fw-bold pb-2'>Order type breakdown</h6>
                          <div className='row g-3'>
                            <div className='col-md-6'>
                              <div className={`card ${style.dineInDiv} p-3 h-100`}>
                                <div className='d-flex justify-content-between'>
                                  <label className='fw-bold small'>Dine-In</label>
                                  <div className={style.dineInDot}></div>
                                </div>
                                <div className='d-flex justify-content-between mt-2'>
                                  <span className='text-muted'>Orders</span>
                                  <span>{reportData?.data?.orderTypeBreakdown?.dineIn?.count ?? 0}</span>
                                </div>
                                <div className='d-flex justify-content-between'>
                                  <span className='text-muted'>Total</span>
                                  <span>₹{reportData?.data?.orderTypeBreakdown?.dineIn?.total?.toLocaleString() ?? '0'}</span>
                                </div>
                              </div>
                            </div>
                            <div className='col-md-6'>
                              <div className={`${style.takeAwayDiv} card p-3 h-100`}>
                                <div className='d-flex justify-content-between'>
                                  <label className='fw-bold small'>Take-Away</label>
                                  <div className={style.takeAwayDot}></div>
                                </div>
                                <div className='d-flex justify-content-between mt-2'>
                                  <span className='text-muted'>Orders</span>
                                  <span>{reportData?.data?.orderTypeBreakdown?.takeAway?.count ?? 0}</span>
                                </div>
                                <div className='d-flex justify-content-between'>
                                  <span className='text-muted'>Total</span>
                                  <span>₹{reportData?.data?.orderTypeBreakdown?.takeAway?.total?.toLocaleString() ?? '0'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className='col-lg-6'>
                          <h6 className='fw-bold pb-2'>Cashier Performance</h6>
                          <div className='card'>
                            <div className={`${style.performanceHeader} p-2 small fw-bold`}>
                              Performance Report
                            </div>
                            <div className={`${style.performanceBorder}`}></div>
                            <div className='p-2'>
                              {reportData?.data?.cashierSales &&
                                Object.entries(reportData.data.cashierSales).map(([cashierName, details]) => (
                                  <div key={cashierName} className="d-flex justify-content-between align-items-center p-3 border-bottom">
                                    <div>
                                      <h6 className='fw-bold mb-1 align-items-center'><span className={style.userIcon}><User size={20} /></span> <span>{cashierName ?? 'NA'}</span></h6>
                                      <span className='text-muted small'>{details.orders ?? 0} orders processed</span>
                                    </div>
                                    <span className='fw-bold'>₹{details.total?.toLocaleString() ?? '0'}</span>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className='mb-4'>
                      <h6 className='fw-bold pb-2'>Item sales breakdown</h6>
                      <div className="table-responsive" style={{ borderRadius: "10px", border: "1px solid #E0E0E0", overflow: "hidden" }}>
                        <table className="table table-hover mb-0">
                          <thead className='table-light'>
                            <tr>
                              <th>Item Name</th>
                              <th className='text-center'>Quantity Sold</th>
                              <th className='text-end'>Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData?.data?.itemSales &&
                              Object.entries(reportData?.data?.itemSales).map(([itemName, details], idx) => (
                                <tr key={itemName} style={{ backgroundColor: idx % 2 !== 0 ? "#f8f9fa" : "white" }}>
                                  <td>{itemName}</td>
                                  <td className="text-center">
                                    <span
                                      className={style.dailyTableQuantity}
                                    >
                                      {details.quantity ?? 0}
                                    </span>
                                  </td>
                                  <td className='text-end'>₹ {details.total?.toLocaleString() ?? '0'}</td>
                                </tr>
                              ))
                            }
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )
                }
              </div>
            </div>
          </Tab>

          {APP_CONFIG.addCustomer && <Tab eventKey="Customer-Search" title="Customer Search">
            <div className='pt-3'>
              <h5 className='fw-bold mb-4'>Sales Report By Mobile Number</h5>

              <div className='card p-4 mb-4'>
                <div className='row g-3 align-items-center'>
                  <div className='col-md-8'>
                    <label className='form-label'>Customer Mobile Number</label>
                    <input
                      value={mobileNumber}
                      className='form-control'
                      placeholder='Enter mobile number'
                      type="text"
                      onChange={(e) => setMobileNumber(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") fetchByMobileNumber() }}
                    />
                  </div>
                  <div style={{ paddingTop: "30px" }} className='col-md-4 d-flex align-items-end gap-2'>
                    <Button variant='primary' onClick={fetchByMobileNumber} className='flex-grow-1'>
                      Generate Report
                    </Button>
                    <Button variant='outline-danger'>
                      <ArrowDownToDot size={18} />
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                {
                  !personData || personData?.data?.totalOrders === 0 ? (
                    <div className={`${style.selectRangeText} card p-5 text-center`}><p className='mb-0'>Enter mobile number of the customer to get report</p></div>
                  ) : (
                    <div className='pt-3'>
                      <div className='d-flex justify-content-between align-items-center mb-4'>
                        <h5 className='fw-bold mb-0'>Sales report of {mobileNumber}</h5>
                        <Button variant='success' size='sm'>
                          <Download size={18} className='me-2' />
                          Export Report
                        </Button>
                      </div>

                      <div className='mb-4'>
                        <div className='row g-3 mb-4'>
                          <div className='col-md-6'>
                            <div className={`${style.totalSalesDiv} card p-3 h-100`}>
                              <p className={`${style.totalSalesText}  small mb-1 fw-bold`}>Total sales</p>
                              <h4 className={`${style.totalSalesValue} fw-bold`}>₹{personData?.data?.totalSales?.toLocaleString() ?? '0'}</h4>
                            </div>
                          </div>
                          <div className='col-md-6'>
                            <div className={`${style.totalOrdersDiv} card p-3 h-100`}>
                              <p className={`${style.totalOrdersText} small mb-1 fw-bold`}>Total Orders</p>
                              <h4 className={`${style.totalOrderValue} fw-bold`}>{personData?.data?.totalOrders?.toLocaleString() ?? '0'}</h4>
                            </div>
                          </div>
                        </div>

                        <div className='row g-4'>
                          <div className='col-lg-6'>
                            <h6 className='fw-bold pb-2'>Order type breakdown</h6>
                            <div className='row g-3'>
                              <div className='col-md-6'>
                                <div className={`card ${style.dineInDiv} p-3 h-100`}>
                                  <div className='d-flex justify-content-between'>
                                    <label className='fw-bold small'>Dine-In</label>
                                    <div className={style.dineInDot}></div>
                                  </div>
                                  <div className='d-flex justify-content-between mt-2'>
                                    <span className='text-muted'>Orders</span>
                                    <span>{personData?.data?.orderTypeBreakdown?.dineIn?.count ?? 0}</span>
                                  </div>
                                  <div className='d-flex justify-content-between'>
                                    <span className='text-muted'>Total</span>
                                    <span>₹{personData?.data?.orderTypeBreakdown?.dineIn?.total?.toLocaleString() ?? '0'}</span>
                                  </div>
                                </div>
                              </div>
                              <div className='col-md-6'>
                                <div className={`${style.takeAwayDiv} card p-3 h-100`}>
                                  <div className='d-flex justify-content-between'>
                                    <label className='fw-bold small'>Take-Away</label>
                                    <div className={style.takeAwayDot}></div>
                                  </div>
                                  <div className='d-flex justify-content-between mt-2'>
                                    <span className='text-muted'>Orders</span>
                                    <span>{personData?.data?.orderTypeBreakdown?.takeAway?.count ?? 0}</span>
                                  </div>
                                  <div className='d-flex justify-content-between'>
                                    <span className='text-muted'>Total</span>
                                    <span>₹{personData?.data?.orderTypeBreakdown?.takeAway?.total?.toLocaleString() ?? '0'}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className='mb-4'>
                        <h6 className='fw-bold pb-2'>Item sales breakdown</h6>
                        <div className="table-responsive" style={{ borderRadius: "10px", border: "1px solid #E0E0E0", overflow: "hidden" }}>
                          <table className="table table-hover mb-0">
                            <thead className='table-light'>
                              <tr>
                                <th>Item Name</th>
                                <th className='text-center'>Quantity Sold</th>
                                <th className='text-end'>Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {personData?.data?.itemSales &&
                                Object.entries(personData?.data?.itemSales).map(([itemName, details], idx) => (
                                  <tr key={itemName} style={{ backgroundColor: idx % 2 !== 0 ? "#f8f9fa" : "white" }}>
                                    <td>{itemName ?? 'NA'}</td>
                                    <td className="text-center">
                                      <span
                                        className={style.dailyTableQuantity}
                                      >
                                        {details.quantity ?? 0}
                                      </span>
                                    </td>
                                    <td className='text-end'>₹ {details.total?.toLocaleString() ?? '0'}</td>
                                  </tr>
                                ))
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Shows customer data that is fetched from backend using api */}
                      {personData?.data?.orders?.length > 0 && (
                        <div className='mb-4'>
                          <h6 className='fw-bold pb-2'>Order Details</h6>
                          <div className='d-flex flex-wrap gap-3'>
                            {personData.data.orders.map((order, index) => (
                              <div
                                key={order._id}
                                className={`${style.orderDetails} p-3 shadow-sm bg-white`}
                              >
                                {/* Header */}
                                <div className={`d-flex justify-content-between align-items-start text-white p-3 rounded ${style.invoiceBackGround}`} style={{ borderRadius: '10px' }}>
                                  <div>
                                    <h6 className="fw-bold mb-1">#{index + 1} - {order.orderType}</h6>
                                    <small className="d-block">Order placed successfully</small>
                                    <div className="mt-2 small">
                                      <div><strong>Invoice:</strong> {order.invoiceNumber ?? 'NA'}</div>
                                      <div><strong>Date:</strong> {new Date(order.createdAt).toLocaleString() ?? 'NA'}</div>
                                      <div><strong>Cashier:</strong> {order.cashier?.name ?? 'NA'}</div>
                                    </div>
                                  </div>
                                  <span className="badge bg-success px-3 py-2 rounded-pill">Completed</span>
                                </div>

                                {/* Items Ordered */}
                                <div className="mt-3">
                                  <h6 className="mb-2 text-secondary">
                                    <i className="bi bi-receipt"></i> Items Ordered
                                  </h6>
                                  <div className="bg-light p-2 rounded">
                                    {order.items.map(item => (
                                      <div
                                        key={item._id}
                                        className="d-flex justify-content-between border-bottom py-1"
                                      >
                                        <div>
                                          <div>{item.productName}</div>
                                          <small className="text-muted">Quantity: {item.quantity}</small>
                                        </div>
                                        <div>₹ {item.price}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Order Summary */}
                                <div className="mt-3">
                                  <h6 className="mb-2 text-secondary">Order Summary</h6>
                                  <div className="d-flex justify-content-between">
                                    <span>Subtotal</span>
                                    <span>₹ {order.subtotal ?? 0}</span>
                                  </div>
                                  <div className="d-flex justify-content-between">
                                    <span>Tax</span>
                                    <span>₹ {order.tax ?? 0}</span>
                                  </div>
                                  <hr />
                                  <div className="d-flex justify-content-between fw-bold">
                                    <span>Total</span>
                                    <span>₹ {order.total ?? 0}</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                }

              </div>
            </div>
          </Tab>}
        </Tabs>
      </div>
    </div>
  )
}

export default Reports
